import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useLang } from '../context/LangContext';
import { useSession } from '../context/SessionContext';
import { STATUS_META, displayStatusKey, payMethodLabel, payStatusLabel, isUnpaidOnline } from '../lib/format';
import { EmptyState, ErrorBanner, Spinner } from '../lib/ui/Feedback';
import { Input, PhoneInput } from '../lib/ui/Field';
import Button from '../lib/ui/Button';
import PageHeader from '../lib/ui/PageHeader';

const API = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');

export default function OrdersPage() {
  const { lang } = useLang();
  const te = lang === 'te';
  const { sessionUser } = useSession();
  const navigate = useNavigate();
  const isAuthed = !!sessionUser?.token;

  const [phone, setPhone] = useState(sessionUser?.phone ? sessionUser.phone.replace(/^\+91/, '') : '');
  const [orderNo, setOrderNo] = useState('');
  const [orders, setOrders] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const authHeaders = () => (sessionUser?.token ? { Authorization: `Bearer ${sessionUser.token}` } : {});

  const fetchAuthed = async (phoneNum) => {
    setLoading(true); setError('');
    try {
      const res = await axios.get(`${API}/api/orders/track/${encodeURIComponent(phoneNum)}`, { headers: authHeaders() });
      setOrders(res.data.orders || []);
    } catch {
      setOrders([]);
      setError(te ? 'ఆర్డర్లు లోడ్ అవలేదు. మళ్ళీ ప్రయత్నించండి.' : 'Could not load orders. Try again.');
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (isAuthed) fetchAuthed(sessionUser.phone);
  }, [isAuthed, sessionUser?.phone]);

  const normalizeNum = (raw) => {
    const digits = String(raw || '').replace(/\D/g, '');
    if (digits.length === 10) return `+91${digits}`;
    if (digits.length === 12 && digits.startsWith('91')) return `+${digits}`;
    if (digits.length === 11 && digits.startsWith('0')) return `+91${digits.slice(1)}`;
    return `+91${digits.slice(-10)}`;
  };

  const guestLookup = async () => {
    const raw = phone.trim();
    if (!raw) { setError(te ? 'ఫోన్ నంబర్ నమోదు చేయండి' : 'Enter your phone number'); return; }
    const digits = raw.replace(/\D/g, '');
    if (digits.replace(/^(91|0)/, '').length < 10) { setError(te ? 'సరైన 10-అంకెల నంబర్ నమోదు చేయండి' : 'Enter a valid 10-digit number'); return; }
    const on = String(orderNo || '').trim().toUpperCase();
    if (!on) { setError(te ? 'ఆర్డర్ నంబర్ + ఫోన్ ఇవ్వండి' : 'Enter order number + phone to track'); setOrders([]); return; }
    setLoading(true); setError('');
    try {
      const res = await axios.get(`${API}/api/orders/lookup`, { params: { order_number: on, phone: normalizeNum(raw) } });
      setOrders(res.data.orders || []);
    } catch {
      setError(te ? 'ఆర్డర్లు కనుగొనబడలేదు.' : 'Could not find orders.');
      setOrders([]);
    } finally { setLoading(false); }
  };

  const openOrder = (order) => navigate(`/orders/${order.id}`, { state: { order } });

  return (
    <div className="max-w-2xl mx-auto">
      <PageHeader title={te ? 'నా ఆర్డర్లు' : 'My Orders'} fallback="/" />
      <div className="px-4 md:px-6 py-4">
        {!isAuthed ? (
          <div className="bg-white rounded-2xl border border-black/5 shadow-soft p-4 mb-4">
            <Input
              className="mb-2"
              placeholder={te ? 'ఆర్డర్ నంబర్ (NRK…)' : 'Order number (NRK…)'}
              value={orderNo}
              onChange={(e) => setOrderNo(e.target.value.toUpperCase())}
              onKeyDown={(e) => e.key === 'Enter' && guestLookup()}
            />
            <div className="flex gap-2">
              <PhoneInput
                value={phone}
                onChange={(e) => !sessionUser?.phone && setPhone(e.target.value.replace(/[^0-9 ]/g, ''))}
                onKeyDown={(e) => e.key === 'Enter' && guestLookup()}
              />
              <Button variant="accent" loading={loading} onClick={guestLookup}>{te ? 'వెతకు' : 'Track'}</Button>
            </div>
            <div className="text-[11px] text-ink-faint mt-2">{te ? 'గెస్ట్: ఆర్డర్ నంబర్ + ఫోన్ ఇవ్వండి, లేదా' : 'Guest: enter order number + phone, or'} <button type="button" onClick={() => navigate('/signup')} className="text-brand font-bold underline">{te ? 'లాగిన్ చేయండి' : 'sign in'}</button></div>
          </div>
        ) : null}

        <ErrorBanner>{error}</ErrorBanner>

        {loading ? (
          <div className="flex justify-center py-14"><Spinner /></div>
        ) : orders === null ? null : orders.length === 0 ? (
          <EmptyState icon="📭" title={te ? 'ఆర్డర్లు కనుగొనబడలేదు' : 'No orders found'} />
        ) : (
          <div className="space-y-3">
            {orders.map((order) => {
              const meta = STATUS_META[displayStatusKey(order)] || STATUS_META.pending;
              const items = order.items || [];
              return (
                <button
                  key={order.id}
                  type="button"
                  onClick={() => openOrder(order)}
                  className="w-full text-left bg-white rounded-2xl border border-black/5 shadow-soft p-4"
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="font-black text-brand-dark">#{order.order_number}</div>
                      <div className="text-xs text-ink-faint mt-0.5">{new Date(order.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</div>
                    </div>
                    <div className="text-right">
                      <div className="font-black text-ink">₹{Number(order.total_amount).toFixed(0)}</div>
                      <span
                        className="inline-block mt-1 text-[11px] font-extrabold px-2 py-0.5 rounded-full"
                        style={{ background: meta.bg, color: meta.color }}
                      >
                        {meta.icon} {te ? meta.labelTe : meta.label}
                      </span>
                    </div>
                  </div>
                  <div className="mt-2 pt-2 border-t border-black/5 space-y-0.5">
                    {items.slice(0, 2).map((item, i) => (
                      <div key={i} className="text-xs text-ink-soft">• {item.product_name}: {item.quantity_kg}{item.unit || 'kg'} = ₹{item.subtotal}</div>
                    ))}
                    {items.length > 2 ? <div className="text-xs text-brand font-bold">+{items.length - 2} more · tap to view all</div> : null}
                  </div>
                  {order.payment_method ? (
                    <div className="text-[11px] text-ink-faint mt-1.5">
                      {payMethodLabel(order.payment_method)} · <span className={`font-extrabold ${isUnpaidOnline(order) ? 'text-gold' : 'text-emerald-700'}`}>{payStatusLabel(order)}</span>
                    </div>
                  ) : null}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
